/**
 * @file Popover 气泡卡片
 * @description 基于 antd Popover 封装，支持 title / titleExtra / content / footer 四区域
 */
import {Popover as AntdPopover} from 'antd';
import type {PopoverProps as AntdPopoverProps} from 'antd';
import {createCx, mergeSemanticClassNames} from '@utils/cx';
import styles from './index.module.less';

const cx = createCx(styles);

export interface PopoverProps extends Omit<AntdPopoverProps, 'title' | 'content'> {
    title?: React.ReactNode;
    content?: React.ReactNode;
    /** 标题行右侧操作区（如复制按钮） */
    titleExtra?: React.ReactNode;
    /** 底部操作区（如取消/确定按钮组） */
    footer?: React.ReactNode;
}

const Popover: React.FC<PopoverProps> = ({
    classNames,
    title,
    titleExtra,
    content,
    footer,
    ...restProps
}) => {
    const mergedClassNames = mergeSemanticClassNames({root: cx('root')}, classNames);

    const titleNode =
        title || titleExtra ? (
            <div className={cx('titleRow')}>
                {title}
                {titleExtra && <span className={cx('titleExtra')}>{titleExtra}</span>}
            </div>
        ) : undefined;

    const contentNode = footer ? (
        <>
            {content}
            <div className={cx('footer')}>{footer}</div>
        </>
    ) : (
        content
    );

    return (
        <AntdPopover
            arrow={false} // 默认不显示箭头
            title={titleNode}
            content={contentNode}
            classNames={mergedClassNames}
            {...restProps}
        />
    );
};

export const PlainPopover: React.FC<PopoverProps> = ({classNames, ...props}) => (
    <Popover {...props} classNames={mergeSemanticClassNames({root: cx('plain')}, classNames)} />
);

/**
 * 预览图类型的 Popover，区别：标题和图片间距为 12px
 */
export const PreviewPopover: React.FC<PopoverProps> = ({classNames, ...props}) => (
    <Popover {...props} classNames={mergeSemanticClassNames({root: cx('preview')}, classNames)} />
);

/**
 * 玻璃拟态 Popover（半透明背景 + backdrop 模糊 + 阴影）
 */
export const BlurPopover: React.FC<PopoverProps> = ({classNames, ...props}) => (
    <Popover {...props} classNames={mergeSemanticClassNames({root: cx('blur')}, classNames)} />
);

export default Popover;
